/**
 * @file SolutionPage.jsx
 * @description 솔루션 탭 라우터 페이지입니다.
 * withGMP 탭: WithGMPOverview 컴포넌트를 렌더링합니다.
 * RWS/MES/WMS 탭: 포트폴리오 + 요약 카드 레이아웃을 렌더링합니다.
 */

import { useEffect, useRef, useState } from 'react';
import { Database, Settings, Target } from 'lucide-react';
import { solMes as mesImg, solRws as rwsImg, solWms as wmsImg } from '../../assets';
import PageHero from '../../components/common/PageHero';
import { solutionItems } from '../../constants/solutionData';
import WithEAIOverview from './WithEAIOverview';
import WithCMMSOverview from './WithCMMSOverview';
import WithFEMSOverview from './WithFEMSOverview';
import WithGMPOverview from './WithGMPOverview';
import WithAIGMPOverview from './WithAIGMPOverview';
import WithRWSOverview from './WithRWSOverview';
import WithMESOverview from './WithMESOverview';
import WithWMSOverview from './WithWMSOverview';

/* 솔루션 브랜드 탭 — solutionItems(RWS/MES/WMS)와 별도로 관리됩니다. */
const WITHGMP_TAB   = { id: 'withgmp',   navLabel: 'withGMP',     href: '/solution/withgmp.html' };
const WITHEAI_TAB   = { id: 'witheai',   navLabel: 'withEAI',     href: '/solution/witheai.html' };
const CMMS_TAB      = { id: 'cmms',      navLabel: 'withCMMS',    href: '/solution/cmms.html' };
const FEMS_TAB      = { id: 'fems',      navLabel: 'withFEMS',    href: '/solution/fems.html' };
const WITHAIGMP_TAB = { id: 'withaigmp', navLabel: 'with-AI-GMP', href: '/solution/withaigmp.html' };

/* withGMP를 제외한 전체 상세 탭 목록 (sol-detail-tabs에 표시) — with-AI-GMP는 GNB 서브메뉴로 별도 진입합니다. */
const ALL_DETAIL_TABS = [...solutionItems, WITHEAI_TAB, CMMS_TAB, FEMS_TAB];

const SOLUTION_IMAGES = {
  rws: rwsImg,
  mes: mesImg,
  wms: wmsImg,
};

/* 요약 카드의 아이콘: 핵심 포인트·운영 방식·데이터 관리 순서 */
const SUMMARY_ICONS = [Target, Settings, Database];

/* ──────────────────────────────────────────────
   핵심 모듈 아이콘: icon 타입 문자열에 따라 SVG 렌더링
   lucide-react에 없는 특수 아이콘(저울, 스캔 등)은 직접 path로 정의합니다.
   ────────────────────────────────────────────── */
function KeyModuleIcon({ type }) {
  const icons = {
    list: (
      <path d="M9 5H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 8 2 2 4-4" />
    ),
    scan: (
      <path d="M3 9V5a2 2 0 0 1 2-2h4M21 9V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4M21 15v4a2 2 0 0 1-2 2h-4M8 7v10M12 7v10M16 7v10" />
    ),
    record: (
      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
    ),
    sign: (
      <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    ),
    chart: <path d="M18 20V10M12 20V4M6 20v-6" />,
    collect: (
      <path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    ),
    alert: (
      <>
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </>
    ),
    location: (
      <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </>
    ),
    support: (
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.86 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.77 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    ),
    tool: (
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    ),
    calendar: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    ),
  };

  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[type] ?? icons.list}
    </svg>
  );
}

/** 핵심 포인트·운영 방식·데이터 관리 3개의 요약 카드를 렌더링합니다. */
function SummaryCards({ items }) {
  return (
    <div className="sol-summary-stack">
      {items.map((item, index) => {
        const Icon = SUMMARY_ICONS[index] ?? Target;
        return (
          <article key={`${item.label}-${item.text}`} className="sol-summary-card">
            <div className="sol-summary-icon" aria-hidden="true">
              <Icon size={30} strokeWidth={1.8} />
            </div>
            <div>
              <h4>{item.label}</h4>
              <p>{item.text}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

/** 주요 기능 또는 도입 효과를 불릿 목록으로 렌더링합니다. */
function BulletList({ items }) {
  return (
    <ul className="sol-card-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

/* ══════════════════════════════════════════════════════
   개별 솔루션 탭 콘텐츠 (RWS / MES / WMS)
   Portfolio(좌텍스트+핵심모듈+우이미지) + 하단 3카드 구조
   ══════════════════════════════════════════════════════ */
function SolutionDetail({ tab, onNavigate }) {
  const tabImage = SOLUTION_IMAGES[tab.id];

  return (
    <>
      {/* ── Portfolio: 텍스트 + 핵심모듈(좌) + 큰 이미지(우) ── */}
      <section className="sol-portfolio">
        <div className="container">
          <div className="sol-portfolio-inner">
            <div className="sol-portfolio-text">
              {/* 솔루션별 이름이 섹션 상단에 명확히 표시되도록 navLabel을 eyebrow로 사용합니다. */}
              <p className="sol-portfolio-eyebrow">{tab.navLabel}</p>
              <h2 className="sol-portfolio-title">
                {tab.portfolioTitle[0]}
                <br />
                {tab.portfolioTitle[1]}
              </h2>
              <p className="sol-portfolio-desc">{tab.portfolioDesc}</p>

              <h3 className="sol-solution-name">{tab.title}</h3>
              <p className="sol-solution-summary">{tab.summary}</p>

              {/* 핵심 모듈 2×2 아이콘 그리드 */}
              <div className="sol-key-modules">
                {tab.keyModules.map((mod) => (
                  <div key={mod.label} className="sol-key-module-item">
                    <div className="sol-key-module-icon">
                      <KeyModuleIcon type={mod.icon} />
                    </div>
                    <span className="sol-key-module-label">{mod.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sol-portfolio-image">
              {tabImage ? (
                <img src={tabImage} alt={`${tab.title} 화면 예시`} />
              ) : (
                <div className="sol-portfolio-placeholder" aria-label="런칭 예정">
                  <span className="sol-placeholder-badge">Coming Soon</span>
                  <p className="sol-placeholder-year">2027</p>
                  <p className="sol-placeholder-text">2027년 런칭 예정</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── 요약 카드: 핵심 포인트 / 주요 기능 / 도입 효과 ── */}
      <section className="sol-cards-section">
        <div className="container">
          <div className="sol-info-grid fade-in">
            <SummaryCards items={tab.highlights} />

            <article className="sol-list-card">
              <h3 className="sol-list-card-title">주요 기능</h3>
              <BulletList items={tab.features} />
            </article>

            <article className="sol-list-card">
              <h3 className="sol-list-card-title">도입 효과</h3>
              <BulletList items={tab.effects} />
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

/* ══════════════════════════════════════════════════════
   SolutionPage: 개요(withGMP) 또는 개별 솔루션 상세 렌더링
   RWS/MES/WMS 탭은 제거 — withGMP 개요의 "자세히 보기"로만 진입합니다.
   ══════════════════════════════════════════════════════ */
export default function SolutionPage({ activeTab, onNavigate }) {
  const allTabs = [WITHGMP_TAB, WITHEAI_TAB, CMMS_TAB, FEMS_TAB, WITHAIGMP_TAB, ...solutionItems];

  /* activeTab이 없거나 withgmp인 경우 withGMP 개요를 기본으로 표시합니다. */
  const currentTab  = allTabs.find((t) => t.id === activeTab) ?? WITHGMP_TAB;
  const isWithGMP   = currentTab.id === 'withgmp';
  /* withEAI 개요 페이지 — withGMP와 동일하게 별도 full-page 뷰로 처리합니다. */
  const isWithEAI   = currentTab.id === 'witheai';
  const isCMMS      = currentTab.id === 'cmms';
  const isFEMS      = currentTab.id === 'fems';
  const isRWS       = currentTab.id === 'rws';
  const isMES       = currentTab.id === 'mes';
  const isWMS       = currentTab.id === 'wms';
  /* with-AI-GMP: withGMP에 AI 챗봇 모듈이 추가된 솔루션 전용 페이지입니다. */
  const isWithAIGMP = currentTab.id === 'withaigmp';

  /* sol-back-nav가 뷰포트를 벗어났을 때 플로팅 사이드 탭을 표시합니다. */
  const [showSideNav, setShowSideNav] = useState(false);
  const backNavRef = useRef(null);

  /* 솔루션 개요 버튼으로 복귀할 때 모듈 구성 섹션으로 스크롤하기 위한 의도를 추적합니다. */
  const scrollToModulesRef = useRef(false);

  /* [기능] sol-back-nav 섹션의 뷰포트 진입/이탈을 감시하여 플로팅 사이드 탭 표시 여부를 전환합니다. */
  useEffect(() => {
    if (isWithGMP || isWithAIGMP) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowSideNav(!entry.isIntersecting),
      { threshold: 0 }
    );
    const el = backNavRef.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [isWithGMP, isWithAIGMP]);

  useEffect(() => {
    if (!isWithGMP || !scrollToModulesRef.current) return;
    scrollToModulesRef.current = false;
    /* withGMP 개요 섹션들이 DOM에 마운트된 이후 실행되므로 offsetTop으로 직접 이동합니다. */
    const el = document.getElementById('wgmp-sec-modules');
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  }, [isWithGMP]);

  /* 모든 솔루션 탭은 각자의 이름을 hero에 직접 표시합니다. */
  const heroTitle = currentTab.navLabel;
  const heroTrail = ['Solution', currentTab.navLabel];


  return (
    <>
      {/* withGMP·with-AI-GMP는 자체 Hero 섹션을 포함하므로 공통 PageHero를 숨깁니다. */}
      {!isWithGMP && !isWithAIGMP && (
        <PageHero
          title={heroTitle}
          trail={heroTrail}
          className="solution-page-hero"
        />
      )}

      {/* withGMP·with-AI-GMP 개요를 제외한 모든 솔루션에서 복귀 버튼 + 전체 탭 표시 */}
      {!isWithGMP && !isWithAIGMP && (
        <section className="sol-back-nav" ref={backNavRef}>
          <div className="container">
            <div className="sol-back-nav-inner">
              {/* 솔루션 개요로 복귀할 때 모듈 구성 섹션을 바로 보여주기 위해 스크롤 의도를 설정합니다. */}
              <button
                type="button"
                className="sol-back-btn"
                onClick={() => { scrollToModulesRef.current = true; onNavigate(WITHGMP_TAB.href); }}
              >
                ← 솔루션 개요
              </button>

              <div className="sol-detail-tabs" role="tablist" aria-label="솔루션 전환">
                {ALL_DETAIL_TABS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={currentTab.id === item.id}
                    data-sol={item.id}
                    className={`sol-detail-tab${currentTab.id === item.id ? ' is-active' : ''}`}
                    onClick={() => onNavigate(item.href)}
                  >
                    {item.navLabel}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* [기능] sol-back-nav가 뷰포트를 벗어나면 오른쪽 고정 위치에 솔루션 전환 탭을 표시합니다. */}
      {showSideNav && !isWithGMP && !isWithAIGMP && (
        <nav className="sol-float-side-nav" aria-label="솔루션 빠른 전환">
          <button
            type="button"
            className="sol-float-back-btn"
            title="솔루션 개요로 돌아가기"
            onClick={() => { scrollToModulesRef.current = true; onNavigate(WITHGMP_TAB.href); }}
          >
            ←
          </button>
          {ALL_DETAIL_TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-sol={item.id}
              className={`sol-float-tab${currentTab.id === item.id ? ' is-active' : ''}`}
              onClick={() => onNavigate(item.href)}
              title={item.navLabel}
            >
              {item.navLabel}
            </button>
          ))}
          <button
            type="button"
            className="sol-float-top-btn"
            title="맨 위로"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            ↑
          </button>
        </nav>
      )}

      {isWithGMP    && <WithGMPOverview   onNavigate={onNavigate} />}
      {isWithEAI    && <WithEAIOverview   onNavigate={onNavigate} />}
      {isCMMS       && <WithCMMSOverview />}
      {isFEMS       && <WithFEMSOverview />}
      {isRWS        && <WithRWSOverview />}
      {isMES        && <WithMESOverview />}
      {isWMS        && <WithWMSOverview />}
      {isWithAIGMP  && <WithAIGMPOverview onNavigate={onNavigate} />}
      {!isWithGMP && !isWithEAI && !isCMMS && !isFEMS && !isRWS && !isMES && !isWMS && !isWithAIGMP && <SolutionDetail tab={currentTab} onNavigate={onNavigate} />}
    </>
  );
}
